import type { CanvasKit, TypefaceFontProvider } from 'canvaskit-wasm';
import { ParagraphText } from '@flobi/artwork-core/render';
// From the engine package: the same bytes the render service embeds in a print PDF.
import interRegular from '@flobi/artwork-core/fonts/Inter-Regular.ttf?url';
import interSemiBold from '@flobi/artwork-core/fonts/Inter-SemiBold.ttf?url';
import arabicRegular from '@flobi/artwork-core/fonts/NotoSansArabic-Regular.ttf?url';
import arabicBold from '@flobi/artwork-core/fonts/NotoSansArabic-Bold.ttf?url';
import { rememberFace } from './faceFiles';

/**
 * The faces the editor draws with.
 *
 * Two are always here: Inter covers Latin, Noto Sans Arabic covers Arabic, Persian and
 * Urdu. They are bundled from the engine package rather than a CDN so the editor works
 * offline, and so the print writer can embed the very bytes the canvas drew with.
 *
 * The rest arrive while the file is open. A font manager built from a fixed list of
 * bytes cannot be added to, so this is a TypefaceFontProvider instead — Skia's own way
 * of registering a face at runtime — and a font picked from the catalogue is fetched,
 * registered under its family, and drawn with on the next frame. Nothing is installed
 * anywhere; the document carries the urls and every reader loads the same files.
 */

const BUILT_IN: { family: string; url: string; weight: number }[] = [
  { family: 'Inter', url: interRegular, weight: 400 },
  { family: 'Inter', url: interSemiBold, weight: 600 },
  { family: 'Noto Sans Arabic', url: arabicRegular, weight: 400 },
  { family: 'Noto Sans Arabic', url: arabicBold, weight: 700 },
];

/**
 * The weights a bundled family really has, or null for any other family.
 *
 * The Weight menu offered Thin to Black for Inter, which is never in the document's font
 * map, and every choice drew as Regular or SemiBold — the two static faces bundled here
 * (QA 2026-09-24, text-F14).
 */
export function builtInWeights(family: string): number[] | null {
  const weights = BUILT_IN.filter((face) => face.family === family).map((face) => face.weight);
  return weights.length ? weights : null;
}

/** The family a text node gets when it doesn't name one. */
export const DEFAULT_FAMILY = 'Inter';

/** The family behind every other one, so Arabic shapes wherever it is typed. */
export const ARABIC_FAMILY = 'Noto Sans Arabic';

let loading: Promise<TypefaceFontProvider | null> | null = null;

/** The faces the editor ships with, loaded once per page. */
export function loadFonts(ck: CanvasKit): Promise<TypefaceFontProvider | null> {
  loading ??= (async () => {
    const provider = ck.TypefaceFontProvider.Make();
    if (!provider) return null;
    await Promise.all(
      BUILT_IN.map(async ({ family, url }) => {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Could not load ${url}: ${response.status}`);
        const bytes = await response.arrayBuffer();
        provider.registerFont(bytes, family);
        rememberFace(family, bytes);
      }),
    );
    return provider;
  })().catch((err: unknown) => {
    // Forgotten, so the next open tries again (files-doc-F8).
    loading = null;
    throw err;
  });
  return loading;
}

/** The text renderer the painter draws with, once the faces are in. */
export async function loadText(ck: CanvasKit): Promise<ParagraphText | null> {
  const fonts = await loadFonts(ck);
  // Arabic sits behind every family: typing Arabic into a box set in Inter shapes
  // properly instead of coming out as empty boxes.
  return fonts ? new ParagraphText(ck, fonts, { fallbacks: [ARABIC_FAMILY] }) : null;
}
